// src/services/notificationService.js
import api from './api';

const NOTIFICATIONS_BASE_URL = '/notifications';

export const notificationService = {
    /**
     * Get notifications for current user
     * @param {Object} params - Query parameters
     * @param {number} params.limit - Number of notifications to fetch
     * @param {number} params.offset - Offset for pagination
     * @param {boolean} params.unread_only - Filter for unread notifications only
     * @param {string} params.status - Status filter: all, active, inactive
     */
    getNotifications: async (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.limit) queryParams.append('limit', params.limit);
        if (params.offset) queryParams.append('offset', params.offset);
        if (params.unread_only) queryParams.append('unread_only', params.unread_only);
        if (params.status) queryParams.append('status', params.status);

        const response = await api.get(`${NOTIFICATIONS_BASE_URL}?${queryParams.toString()}`);
        return response.data.data;
    },

    /**
     * Get unread notification count
     */
    getUnreadCount: async () => {
        const response = await api.get(`${NOTIFICATIONS_BASE_URL}/unread-count`);
        return response.data.data;
    },

    /**
     * Create a new notification (broadcast)
     * @param {Object} notificationData
     * @param {string} notificationData.title - Notification title
     * @param {string} notificationData.message - Notification message
     * @param {string} notificationData.type - Type: info, success, warning, error, announcement
     * @param {string} notificationData.target_type - Target: all, organization, premise
     * @param {number} notificationData.target_org_id - Organization ID (for org broadcasts)
     * @param {number} notificationData.target_premise_id - Premise ID (for premise broadcasts)
     * @param {string} notificationData.action_url - Optional action URL
     * @param {string} notificationData.action_label - Optional action label
     * @param {string} notificationData.expires_at - Optional expiration date
     */
    createNotification: async (notificationData) => {
        const response = await api.post(NOTIFICATIONS_BASE_URL, notificationData);
        return response.data;
    },

    /**
     * Mark a notification as read
     * @param {number} notificationId
     */
    markAsRead: async (notificationId) => {
        const response = await api.put(`${NOTIFICATIONS_BASE_URL}/${notificationId}/read`);
        return response.data;
    },

    /**
     * Mark all notifications as read
     */
    markAllAsRead: async () => {
        const response = await api.put(`${NOTIFICATIONS_BASE_URL}/read-all`);
        return response.data;
    },

    /**
     * Update a notification
     * @param {number} notificationId
     * @param {Object} notificationData
     */
    updateNotification: async (notificationId, notificationData) => {
        const response = await api.put(`${NOTIFICATIONS_BASE_URL}/${notificationId}`, notificationData);
        return response.data;
    },

    /**
     * Delete a notification
     * @param {number} notificationId
     */
    deleteNotification: async (notificationId) => {
        const response = await api.delete(`${NOTIFICATIONS_BASE_URL}/${notificationId}`);
        return response.data;
    }
};

export default notificationService;
